import React from 'react'
import { TabsVariant } from './types'

export interface TabButtonProps {
  title: string
  badge?: string | number
  isActive: boolean
  onClick: () => void
  /** Use flex-1 for equal width tabs, flex-shrink-0 for content-sized tabs */
  flexMode?: 'equal' | 'content'
  variant?: TabsVariant
  className?: string
}

export function TabButton({
  title,
  badge,
  isActive,
  onClick,
  flexMode = 'equal',
  variant = 'underline',
  className = '',
}: TabButtonProps) {
  const flexClass = flexMode === 'equal' ? 'flex-1' : 'flex-shrink-0'

  const underlineClasses = isActive
    ? 'text-fg font-semibold border-b-2 border-green-700'
    : 'text-fg font-light hover:text-fg border-b-2 border-gray-200'

  // Pills: gray-100 container holds tabs; active tab is a rounded white
  // pill with subtle shadow. Inactive tabs are transparent.
  const pillClasses = isActive
    ? 'text-fg font-medium bg-white rounded-md shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'
    : 'text-fg-secondary font-medium hover:text-fg rounded-md'

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        ${flexClass} text-center relative whitespace-nowrap
        transition-colors duration-200
        ${
          variant === 'pills'
            ? `px-3 h-9 text-base ${pillClasses}`
            : `p-2 sm:p-3 text-base ${underlineClasses}`
        }
        ${className}
      `}
    >
      <span className="inline-flex items-center gap-2">
        {title}
        {badge !== undefined && <TabBadge badge={badge} isActive={isActive} />}
      </span>
    </button>
  )
}

interface TabBadgeProps {
  badge: string | number
  isActive: boolean
}

export function TabBadge({ badge, isActive }: TabBadgeProps) {
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {badge}
    </span>
  )
}
